import { useEffect, useRef, useState } from 'react';
import { inWords, remaining } from '../lib/sitting';
import { useAuth } from '../store/auth';
import { candidateMark } from '../lib/branding';

/** The last minute, when the clock turns red and counts seconds. */
const FINAL_MINUTE_MS = 60_000;

/**
 * The choices the real test offers, and only those.
 *
 * Three contrasts and three sizes, named as it names them. A fourth of either
 * is a difference a candidate would notice on the day, and the point of the
 * screen is that they should notice nothing.
 */
export type Scheme = 'bw' | 'wb' | 'yb';
export type TextSize = 'regular' | 'large' | 'xlarge';

export interface Settings {
  scheme: Scheme;
  size: TextSize;
}

export const DEFAULT_SETTINGS: Settings = { scheme: 'bw', size: 'regular' };

const SCHEMES: { id: Scheme; label: string }[] = [
  { id: 'bw', label: 'Black on white' },
  { id: 'wb', label: 'White on black' },
  { id: 'yb', label: 'Yellow on black' },
];

const SIZES: { id: TextSize; label: string }[] = [
  { id: 'regular', label: 'Regular' },
  { id: 'large', label: 'Large' },
  { id: 'xlarge', label: 'Extra large' },
];

/** A half-filled circle: the contrast the screen is set to. */
const ContrastIcon = () => (
  <svg viewBox="0 0 18 18" width="18" height="18" aria-hidden="true" focusable="false">
    <circle cx="9" cy="9" r="7.2" fill="none" stroke="currentColor" strokeWidth="1.4" />
    <path d="M9 1.8a7.2 7.2 0 0 0 0 14.4z" fill="currentColor" />
  </svg>
);

/** A magnifier with a plus: the size of the type. */
const TextSizeIcon = () => (
  <svg viewBox="0 0 18 18" width="18" height="18" aria-hidden="true" focusable="false">
    <circle cx="7.8" cy="7.8" r="5.4" fill="none" stroke="currentColor" strokeWidth="1.4" />
    <path d="M11.8 11.8 16 16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M5.6 7.8h4.4M7.8 5.6v4.4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

/** An i in a circle: what the paper expects of the candidate. */
const InfoIcon = () => (
  <svg viewBox="0 0 18 18" width="18" height="18" aria-hidden="true" focusable="false">
    <circle cx="9" cy="9" r="7.2" fill="currentColor" />
    <path d="M9 7.6v5" stroke="var(--x-bg)" strokeWidth="1.6" strokeLinecap="round" />
    <circle cx="9" cy="5.1" r="0.95" fill="var(--x-bg)" />
  </svg>
);

/**
 * The options screen, which is a screen and not a dropdown.
 *
 * The real test takes the whole window for it: a title, a way back, a way out,
 * and one list of plainly named choices with a tick against the current one.
 * A candidate changing the contrast is usually doing it because they cannot
 * read the screen, and a menu of small buttons in a corner is the wrong answer
 * to that.
 */
type Pane = 'root' | 'contrast' | 'size' | 'instructions' | 'volume';

function OptionsScreen({
  settings,
  onSettings,
  notes,
  volume,
  onVolume,
  initialPane = 'root',
  onClose,
}: {
  settings: Settings;
  onSettings: (next: Settings) => void;
  /** What this paper expects of the candidate, in its own words. */
  notes: string[];
  /** Listening only; null on the papers that have no sound. */
  volume: number | null;
  onVolume: (v: number) => void;
  initialPane?: Pane;
  onClose: () => void;
}) {
  const [pane, setPane] = useState<Pane>(initialPane);
  const title =
    pane === 'contrast'
      ? 'Contrast'
      : pane === 'size'
        ? 'Text size'
        : pane === 'instructions'
          ? 'Test Instructions'
          : pane === 'volume'
            ? 'Volume'
            : 'Options';

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (pane === 'root') onClose();
      else setPane('root');
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pane, onClose]);

  const rows =
    pane === 'contrast'
      ? SCHEMES.map((scheme) => ({
          key: scheme.id,
          label: scheme.label,
          chosen: settings.scheme === scheme.id,
          go: () => onSettings({ ...settings, scheme: scheme.id }),
        }))
      : pane === 'size'
        ? SIZES.map((size) => ({
            key: size.id,
            label: size.label,
            chosen: settings.size === size.id,
            go: () => onSettings({ ...settings, size: size.id }),
          }))
        : [
            ...(volume === null
              ? []
              : [
                  {
                    key: 'volume',
                    label: 'Volume',
                    chosen: false,
                    icon: <SpeakerIcon />,
                    go: () => setPane('volume'),
                  },
                ]),
            {
              key: 'contrast',
              label: 'Contrast',
              chosen: false,
              icon: <ContrastIcon />,
              go: () => setPane('contrast'),
            },
            {
              key: 'size',
              label: 'Text size',
              chosen: false,
              icon: <TextSizeIcon />,
              go: () => setPane('size'),
            },
            {
              key: 'instructions',
              label: 'Test Instructions',
              chosen: false,
              icon: <InfoIcon />,
              go: () => setPane('instructions'),
            },
          ];

  return (
    <div className="xopts" role="dialog" aria-modal="true" aria-label={title}>
      <header className="xopts__bar">
        {pane === 'root' ? (
          <span />
        ) : (
          <button className="xopts__back" onClick={() => setPane('root')}>
            <span aria-hidden="true">❮</span> Options
          </button>
        )}
        <h2 className="xopts__title">{title}</h2>
        <button className="xopts__close" aria-label="Close options" onClick={onClose}>
          ✕
        </button>
      </header>

      {pane === 'volume' ? (
        <div className="xopts__volume">
          <label htmlFor="exam-volume">How loud the recording plays</label>
          <input
            id="exam-volume"
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={volume ?? 0.8}
            autoFocus
            onChange={(e) => onVolume(Number(e.target.value))}
          />
          <p className="xopts__note">
            The recording plays once. Set it where you can hear it comfortably before it goes on.
          </p>
        </div>
      ) : pane === 'instructions' ? (
        <ul className="xopts__list xopts__list--read">
          {notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      ) : (
        <ul className="xopts__list">
          {rows.map((row) => (
            <li key={row.key}>
              <button
                className="xopts__row"
                aria-current={row.chosen || undefined}
                onClick={row.go}
              >
                {'icon' in row && row.icon ? (
                  <span className="xopts__icon">{row.icon}</span>
                ) : (
                  <span className="xopts__tick" aria-hidden="true">
                    {row.chosen ? '✔' : ''}
                  </span>
                )}
                <span>{row.label}</span>
                {pane === 'root' && (
                  <span className="xopts__more" aria-hidden="true">
                    ❯
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Three bars: the menu the real screen keeps its settings behind. */
const MenuIcon = () => (
  <svg viewBox="0 0 20 14" width="20" height="14" aria-hidden="true" focusable="false">
    <g stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M1 1h18M1 7h18M1 13h18" />
    </g>
  </svg>
);

/** A small speaker, beside the words that say whether sound is coming out. */
const SpeakerIcon = () => (
  <svg viewBox="0 0 16 14" width="15" height="13" aria-hidden="true" focusable="false">
    <path d="M1.5 5h2.6L7.4 2.2v9.6L4.1 9H1.5z" fill="currentColor" />
    <g fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
      <path d="M9.8 5a3.4 3.4 0 0 1 0 4" />
      <path d="M11.8 3.2a6 6 0 0 1 0 7.6" />
    </g>
  </svg>
);

/** Ten minutes, then five — the two warnings the real exam gives. */
function warnLevel(ms: number): 'none' | 'soon' | 'urgent' | 'final' {
  if (ms <= FINAL_MINUTE_MS) return 'final';
  if (ms <= 5 * 60_000) return 'urgent';
  if (ms <= 10 * 60_000) return 'soon';
  return 'none';
}

interface TopBarProps {
  candidateName: string;
  candidateNumber: string | null;
  msRemaining: number;
  settings: Settings;
  onSettings: (next: Settings) => void;
  volume: number | null;
  onVolume: (v: number) => void;
  /** What this paper expects of the candidate, shown under Test Instructions. */
  notes: string[];
  /**
   * Whether the recording is still running. Listening only; null elsewhere.
   *
   * Deliberately says nothing about how far through it is: the position is
   * exactly what a candidate is not allowed to know, and a progress bar would
   * be a scrubber they could read even if not drag. It reports only that
   * sound should be coming out, which is what someone with a dead headphone
   * or the volume down needs to be told.
   */
  /**
   * Sound is coming out, or nothing.
   *
   * There used to be a 'finished' state here, reading "Audio has finished" in
   * the bar. The real screen says no such thing, and a candidate does not need
   * telling — they were listening to it. What replaces it is the box saying
   * how long they have left, which is the thing they actually wanted to know.
   */
  recording: 'playing' | null;
  /** False while a Listening recording is still playing; see ExamPage. */
  showClock?: boolean;
}

export function ExamTopBar({
  candidateName,
  candidateNumber,
  msRemaining,
  settings,
  onSettings,
  volume,
  onVolume,
  recording,
  notes,
  showClock = true,
}: TopBarProps) {
  const [menu, setMenu] = useState<Pane | null>(null);
  const org = useAuth((state) => state.organization);
  const mark = candidateMark(org);
  const warn = warnLevel(msRemaining);
  const announced = useRef<Set<string>>(new Set());

  // Announced once each, for candidates using a screen reader — the colour
  // change alone is not a warning everyone receives.
  const [announcement, setAnnouncement] = useState('');
  useEffect(() => {
    if (!showClock || warn === 'none' || announced.current.has(warn)) return;
    announced.current.add(warn);
    /*
     * The time actually left, not the name of the threshold crossed. A clock
     * ticking down does cross them in order, and there the two agree. A
     * Listening paper's does not: it appears at 1m 50s when the recording
     * stops, and a fixed label would have announced "5 minutes remaining" to
     * a candidate with under two.
     */
    setAnnouncement(inWords(msRemaining).replace(', 0 seconds', ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warn, showClock]);

  return (
    <header className="exam__top">
      <div className="exam__left">
        {/* The centre's logo where it has one, its name where it has not, and
            nothing at all for the platform's own workspace — see candidateMark.
            The wordmark that used to sit here belongs to an examination board,
            not to this platform or to the centre using it. */}
        {mark && (
          <span className="exam__mark">
            {'logoUrl' in mark ? (
              <img src={mark.logoUrl} alt={mark.alt} />
            ) : (
              <span className="exam__centre">{mark.name}</span>
            )}
          </span>
        )}

        <div className="exam__who">
        {/*
          * The space is deliberate. The number is set apart by a margin, which
          * a sighted reader sees and a screen reader does not: without it the
          * line is announced, and copied, as "Student 42848830".
          */}
        <p className="exam__candidate">
          {candidateName}
          {candidateNumber && <span> {candidateNumber}</span>}
        </p>
        {/* Under the name, as the real bar carries it: whether sound is
            coming out, and nothing about how far through it is. */}
        {recording && (
          <p className={`exam__recording exam__recording--${recording}`}>
            <SpeakerIcon />
            Audio is Playing
          </p>
        )}
        </div>
      </div>

      {/*
        * The clock, in the middle of the bar and there for the whole paper.
        *
        * It used to appear only for the last two minutes, on the reasoning
        * that a candidate should work to the paper rather than to a number.
        * But a Reading paper is an hour long and a candidate has to portion
        * that hour across three passages themselves — with no clock they are
        * budgeting blind, and the first they learn of the time is when there
        * is none.
        *
        * The clock and nothing else. It carried the word "left" beside it,
        * which a candidate reads once and never again — and in the last
        * minute, when the whole thing turns red and beats, a word attached to
        * it is one more thing moving in the corner of their eye.
        */}
      <p className="exam__timer" data-warn={warn === 'none' || !showClock ? undefined : warn}>
        {showClock && <span>{remaining(msRemaining)}</span>}
      </p>

      <div className="exam__tools">
        <p aria-live="polite" className="sr-only">
          {announcement}
        </p>

        {/* Icons, as the real bar carries them, and each one does something:
            the speaker opens the volume, the menu opens the options. A bell
            and a notepad drawn to match would do nothing at all. */}
        {volume !== null && (
          <button
            className="exam__icon"
            aria-label="Volume"
            title="Volume"
            onClick={() => setMenu('volume')}
          >
            <SpeakerIcon />
          </button>
        )}

        <button
          className="exam__icon"
          aria-label="Options"
          title="Options"
          onClick={() => setMenu('root')}
        >
          <MenuIcon />
        </button>

        {menu && (
          <OptionsScreen
            settings={settings}
            onSettings={onSettings}
            notes={notes}
            volume={volume}
            onVolume={onVolume}
            initialPane={menu}
            onClose={() => setMenu(null)}
          />
        )}
      </div>
    </header>
  );
}

interface NavProps {
  parts: { index: number; numbers: number[] }[];
  currentPart: number;
  current: number;
  answered: Set<number>;
  flagged: Set<number>;
  /**
   * Whether the open part offers its questions as numbered buttons.
   *
   * False for Writing: one question the length of an essay is not something a
   * candidate navigates to, and the real screen's strip carries the two parts
   * alone.
   */
  numbered?: boolean;
  onPart: (index: number) => void;
  onGo: (number: number) => void;
}

export function ExamNav({
  parts,
  currentPart,
  current,
  answered,
  flagged,
  numbered = true,
  onPart,
  onGo,
}: NavProps) {
  const all = parts.flatMap((p) => p.numbers);
  const position = all.indexOf(current);

  return (
    <nav className="exam__bottom" aria-label="Questions">
      {/*
        * Forward and back, above the strip and to the right, as the real
        * screen places them — and the only two controls here. There is no
        * submit: a paper ends when its clock does, which is what the exam
        * itself does and what makes the timing the same for everyone.
        */}
      <div className="exam__movers">
        <button
          className="exam__arrow"
          aria-label="Previous question"
          disabled={position <= 0}
          onClick={() => onGo(all[position - 1]!)}
        >
          ←
        </button>
        <button
          className="exam__arrow exam__arrow--next"
          aria-label="Next question"
          disabled={position < 0 || position >= all.length - 1}
          onClick={() => onGo(all[position + 1]!)}
        >
          →
        </button>
      </div>

      {/*
        * The part being sat shows its questions; the others show how many of
        * theirs are answered. A candidate glancing down sees where they are
        * and what is left, without forty numbered buttons competing for it.
        */}
      <div className="exam__parts">
        {parts.map((p) => {
          const done = p.numbers.filter((n) => answered.has(n)).length;
          if (p.index !== currentPart) {
            return (
              <button key={p.index} className="exam__part" onClick={() => onPart(p.index)}>
                <span className="exam__partname">Part {p.index}</span>
                <span className="exam__partcount">
                  {done} of {p.numbers.length}
                </span>
              </button>
            );
          }
          return (
            <div key={p.index} className="exam__part exam__part--open">
              {numbered && (
                <span className="exam__track" aria-hidden="true">
                  {p.numbers.map((n) => (
                    <span
                      key={n}
                      className="exam__pip"
                      data-answered={answered.has(n) || undefined}
                      data-current={n === current || undefined}
                    />
                  ))}
                </span>
              )}
              <span className="exam__partrow">
                <span className="exam__partname">Part {p.index}</span>
                {numbered &&
                  p.numbers.map((n) => (
                    <button
                      key={n}
                      className="exam__num"
                      aria-current={n === current || undefined}
                      aria-label={`Question ${n}${answered.has(n) ? ', answered' : ''}${
                        flagged.has(n) ? ', flagged for review' : ''
                      }`}
                      data-answered={answered.has(n) || undefined}
                      data-flagged={flagged.has(n) || undefined}
                      onClick={() => onGo(n)}
                    >
                      {n}
                    </button>
                  ))}
              </span>
            </div>
          );
        })}
      </div>
    </nav>
  );
}
